import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../services/api';

/**
 * Content Pipeline Dashboard
 * 
 * Dashboard for monitoring and managing automated content generation pipelines.
 * Features:
 * - Pipeline list with status filtering
 * - Pipeline creation wizard
 * - Stage progress visualization
 * - Asset preview and approval
 * - Analytics overview
 */

// Pipeline stages with their metadata
const PIPELINE_STAGES = [
  { id: 'brief_analysis', label: 'Brief Analysis', icon: '📋' },
  { id: 'copy_generation', label: 'Copy Generation', icon: '✍️' },
  { id: 'image_prompt_generation', label: 'Image Prompts', icon: '💡' },
  { id: 'image_generation', label: 'Image Generation', icon: '🎨' },
  { id: 'video_script_generation', label: 'Video Scripts', icon: '🎬' },
  { id: 'video_generation', label: 'Video Generation', icon: '📹' },
  { id: 'asset_compilation', label: 'Compilation', icon: '📦' },
  { id: 'review_queue', label: 'Review', icon: '👀' },
  { id: 'completed', label: 'Completed', icon: '✅' }
];

const STATUS_COLORS = {
  pending: '#6b7280',
  in_progress: '#3b82f6',
  paused: '#f59e0b',
  awaiting_approval: '#8b5cf6',
  completed: '#10b981',
  failed: '#ef4444',
  cancelled: '#9ca3af'
};

const STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'In Progress',
  paused: 'Paused',
  awaiting_approval: 'Awaiting Approval',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled'
};

const CONTENT_TYPES = [
  { value: 'social_post', label: 'Social Media Post', icon: '📱' },
  { value: 'blog_article', label: 'Blog Article', icon: '📝' },
  { value: 'email_campaign', label: 'Email Campaign', icon: '📧' },
  { value: 'ad_creative', label: 'Ad Creative', icon: '🎯' },
  { value: 'video_ad', label: 'Video Ad', icon: '🎬' },
  { value: 'explainer_video', label: 'Explainer Video', icon: '🎥' }
];

const PLATFORMS = [
  { value: 'facebook', label: 'Facebook', icon: '📘' },
  { value: 'instagram', label: 'Instagram', icon: '📸' },
  { value: 'linkedin', label: 'LinkedIn', icon: '💼' },
  { value: 'twitter', label: 'Twitter/X', icon: '🐦' },
  { value: 'tiktok', label: 'TikTok', icon: '🎵' },
  { value: 'youtube', label: 'YouTube', icon: '▶️' }
];

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'formal', label: 'Formal' },
  { value: 'playful', label: 'Playful' },
  { value: 'inspirational', label: 'Inspirational' },
  { value: 'urgent', label: 'Urgent' }
];

const STYLE_PRESETS = [
  { value: 'photorealistic', label: 'Photorealistic' },
  { value: 'digital-art', label: 'Digital Art' },
  { value: 'cinematic', label: 'Cinematic' },
  { value: 'minimalist', label: 'Minimalist' },
  { value: '3d-model', label: '3D Render' },
  { value: 'anime', label: 'Anime/Illustration' }
];


const ContentPipeline = () => {
  // State
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('pipelines');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  // New pipeline form state
  const [newPipeline, setNewPipeline] = useState({
    brief: {
      title: '',
      objective: '',
      content_type: 'social_post',
      target_platforms: ['instagram'],
      target_audience: '',
      key_messages: [''],
      tone_of_voice: 'professional',
      call_to_action: '',
      brand_colors: [],
      keywords: [],
      additional_notes: ''
    },
    config: {
      auto_generate_images: true,
      auto_generate_videos: false,
      image_style_preset: 'photorealistic',
      video_style: 'modern',
      approval_required: true,
      num_variations: 3
    },
    auto_start: false
  });

  // API Base URL
  const API_BASE = '/api/content-pipeline';

  // Fetch pipelines
  const fetchPipelines = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const response = await authFetch(`${API_BASE}/pipelines?${params}`);
      if (!response.ok) throw new Error('Failed to fetch pipelines');
      
      const data = await response.json();
      setPipelines(data.pipelines || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // Fetch pipeline details
  const fetchPipelineDetails = useCallback(async (pipelineId) => {
    try {
      const response = await authFetch(`${API_BASE}/pipelines/${pipelineId}`);
      if (!response.ok) throw new Error('Failed to fetch pipeline details');
      
      const data = await response.json();
      setSelectedPipeline(data.pipeline);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  // Calculate analytics
  const calculateAnalytics = useCallback(() => {
    if (!pipelines.length) {
      setAnalytics(null);
      return;
    }

    const completed = pipelines.filter(p => p.status === 'completed');
    const failed = pipelines.filter(p => p.status === 'failed');
    const inProgress = pipelines.filter(p => p.status === 'in_progress');
    const awaitingApproval = pipelines.filter(p => p.status === 'awaiting_approval');

    const totalAssets = pipelines.reduce((acc, p) => acc + (p.generated_assets?.length || 0), 0);
    
    const avgProgress = pipelines.reduce((acc, p) => acc + (p.progress_percentage || 0), 0) / pipelines.length;

    setAnalytics({
      total: pipelines.length,
      completed: completed.length,
      failed: failed.length,
      inProgress: inProgress.length,
      awaitingApproval: awaitingApproval.length,
      totalAssets,
      successRate: pipelines.length > 0 ? (completed.length / (completed.length + failed.length) * 100) || 0 : 0,
      avgProgress: Math.round(avgProgress)
    });
  }, [pipelines]);

  useEffect(() => {
    fetchPipelines();
  }, [fetchPipelines]);

  useEffect(() => {
    calculateAnalytics();
  }, [calculateAnalytics]);

  // Create new pipeline
  const createPipeline = async () => {
    try {
      const response = await authFetch(`${API_BASE}/pipelines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPipeline)
      });

      if (!response.ok) throw new Error('Failed to create pipeline');

      const data = await response.json();
      setPipelines(prev => [data.pipeline, ...prev]);
      setShowCreateModal(false);
      setSelectedPipeline(data.pipeline);
      
      // Reset form
      setNewPipeline({
        brief: {
          title: '',
          objective: '',
          content_type: 'social_post',
          target_platforms: ['instagram'],
          target_audience: '',
          key_messages: [''],
          tone_of_voice: 'professional',
          call_to_action: '',
          brand_colors: [],
          keywords: [],
          additional_notes: ''
        },
        config: {
          auto_generate_images: true,
          auto_generate_videos: false,
          image_style_preset: 'photorealistic',
          video_style: 'modern',
          approval_required: true,
          num_variations: 3
        },
        auto_start: false
      });
    } catch (err) {
      setError(err.message);
    }
  };

  // Start pipeline
  const startPipeline = async (pipelineId) => {
    try {
      const response = await authFetch(`${API_BASE}/pipelines/${pipelineId}/run`, {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to start pipeline');

      const data = await response.json();
      setPipelines(prev => prev.map(p => 
        p.pipeline_id === pipelineId ? data.pipeline : p
      ));
      if (selectedPipeline?.pipeline_id === pipelineId) {
        setSelectedPipeline(data.pipeline);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Cancel pipeline
  const cancelPipeline = async (pipelineId) => {
    try {
      const response = await authFetch(`${API_BASE}/pipelines/${pipelineId}/cancel`, {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to cancel pipeline');

      const data = await response.json();
      setPipelines(prev => prev.map(p => 
        p.pipeline_id === pipelineId ? data.pipeline : p
      ));
      if (selectedPipeline?.pipeline_id === pipelineId) {
        setSelectedPipeline(data.pipeline);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Approve/reject asset
  const handleAssetApproval = async (pipelineId, assetId, approved) => {
    try {
      const response = await authFetch(`${API_BASE}/pipelines/${pipelineId}/assets/${assetId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved })
      });

      if (!response.ok) throw new Error('Failed to update asset');

      // Refresh pipeline details
      await fetchPipelineDetails(pipelineId);
      setShowAssetModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  // Bulk approve assets
  const bulkApproveAssets = async (pipelineId, assetIds, approved) => {
    try {
      const response = await authFetch(`${API_BASE}/pipelines/${pipelineId}/review/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_ids: assetIds, approved })
      });

      if (!response.ok) throw new Error('Failed to bulk update assets');

      // Refresh pipeline details
      await fetchPipelineDetails(pipelineId);
    } catch (err) {
      setError(err.message);
    }
  };

  // Get stage index for progress display
  const getStageIndex = (stageId) => {
    return PIPELINE_STAGES.findIndex(s => s.id === stageId);
  };

  // Styles
  const styles = {
    container: {
      padding: '24px',
      maxWidth: '1600px',
      margin: '0 auto',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#1f2937',
      margin: 0
    },
    subtitle: {
      fontSize: '14px',
      color: '#6b7280',
      marginTop: '4px'
    },
    tabs: {
      display: 'flex',
      gap: '8px',
      marginBottom: '24px',
      borderBottom: '1px solid #e5e7eb',
      paddingBottom: '8px'
    },
    tab: {
      padding: '8px 16px',
      borderRadius: '8px',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      color: '#6b7280',
      transition: 'all 0.2s'
    },
    activeTab: {
      background: '#3b82f6',
      color: 'white'
    },
    createButton: {
      padding: '10px 20px',
      background: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    filterBar: {
      display: 'flex',
      gap: '12px',
      marginBottom: '20px',
      flexWrap: 'wrap'
    },
    filterSelect: {
      padding: '8px 12px',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '14px',
      background: 'white',
      cursor: 'pointer'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
      gap: '20px'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      padding: '20px',
      cursor: 'pointer',
      transition: 'box-shadow 0.2s',
      border: '1px solid #e5e7eb'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '16px'
    },
    cardTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1f2937',
      margin: 0
    },
    statusBadge: {
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500',
      color: 'white'
    },
    progressBar: {
      height: '8px',
      background: '#e5e7eb',
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '12px'
    },
    progressFill: {
      height: '100%',
      background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
      borderRadius: '4px',
      transition: 'width 0.3s'
    },
    stagesRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '16px'
    },
    stageIndicator: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      flex: 1
    },
    stageDot: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      marginBottom: '4px'
    },
    stageLabel: {
      fontSize: '10px',
      color: '#6b7280',
      textAlign: 'center',
      maxWidth: '60px'
    },
    cardMeta: {
      display: 'flex',
      gap: '16px',
      fontSize: '13px',
      color: '#6b7280'
    },
    detailPanel: {
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      padding: '24px',
      marginTop: '24px'
    },
    detailHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: '1px solid #e5e7eb'
    },
    detailTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937'
    },
    actionButton: {
      padding: '8px 16px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    primaryButton: {
      background: '#3b82f6',
      color: 'white'
    },
    dangerButton: {
      background: '#ef4444',
      color: 'white'
    },
    secondaryButton: {
      background: '#f3f4f6',
      color: '#374151'
    },
    assetsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '16px',
      marginTop: '20px'
    },
    assetCard: {
      background: '#f9fafb',
      borderRadius: '8px',
      padding: '16px',
      border: '1px solid #e5e7eb'
    },
    assetType: {
      fontSize: '12px',
      fontWeight: '500',
      color: '#6b7280',
      textTransform: 'uppercase',
      marginBottom: '8px'
    },
    assetContent: {
      fontSize: '14px',
      color: '#374151',
      marginBottom: '12px'
    },
    assetActions: {
      display: 'flex',
      gap: '8px'
    },
    approveButton: {
      padding: '6px 12px',
      background: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '12px',
      cursor: 'pointer'
    },
    rejectButton: {
      padding: '6px 12px',
      background: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '12px',
      cursor: 'pointer'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      background: 'white',
      borderRadius: '16px',
      padding: '24px',
      maxWidth: '800px',
      width: '90%',
      maxHeight: '90vh',
      overflow: 'auto'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#6b7280'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '6px'
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '14px',
      minHeight: '100px',
      resize: 'vertical',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '14px',
      background: 'white',
      boxSizing: 'border-box'
    },
    checkboxGroup: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px'
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 12px',
      background: '#f9fafb',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    checkbox: {
      width: '16px',
      height: '16px'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px'
    },
    analyticsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '24px'
    },
    statCard: {
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    statValue: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#1f2937'
    },
    statLabel: {
      fontSize: '14px',
      color: '#6b7280',
      marginTop: '4px'
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#6b7280'
    },
    emptyIcon: {
      fontSize: '48px',
      marginBottom: '16px'
    }
  };

  // Render analytics cards
  const renderAnalytics = () => {
    if (!analytics) return null;

    return (
      <div style={styles.analyticsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{analytics.total}</div>
          <div style={styles.statLabel}>Total Pipelines</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{analytics.completed}</div>
          <div style={styles.statLabel}>Completed</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#3b82f6' }}>{analytics.inProgress}</div>
          <div style={styles.statLabel}>In Progress</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#8b5cf6' }}>{analytics.awaitingApproval}</div>
          <div style={styles.statLabel}>Awaiting Approval</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{analytics.totalAssets}</div>
          <div style={styles.statLabel}>Total Assets</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: analytics.successRate > 80 ? '#10b981' : '#f59e0b' }}>
            {analytics.successRate.toFixed(0)}%
          </div>
          <div style={styles.statLabel}>Success Rate</div>
        </div>
      </div>
    );
  };

  // Render pipeline card
  const renderPipelineCard = (pipeline) => {
    const currentStageIndex = getStageIndex(pipeline.current_stage);
    
    return (
      <div
        key={pipeline.pipeline_id}
        style={styles.card}
        onClick={() => {
          setSelectedPipeline(pipeline);
          fetchPipelineDetails(pipeline.pipeline_id);
        }}
      >
        <div style={styles.cardHeader}>
          <div>
            <h3 style={styles.cardTitle}>{pipeline.brief?.title || 'Untitled Pipeline'}</h3>
            <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
              {CONTENT_TYPES.find(c => c.value === pipeline.brief?.content_type)?.label || pipeline.brief?.content_type}
            </div>
          </div>
          <span
            style={{
              ...styles.statusBadge,
              background: STATUS_COLORS[pipeline.status] || '#6b7280'
            }}
          >
            {STATUS_LABELS[pipeline.status] || pipeline.status}
          </span>
        </div>

        <div style={styles.progressBar}>
          <div
            style={{
              ...styles.progressFill,
              width: `${pipeline.progress_percentage || 0}%`
            }}
          />
        </div>

        <div style={styles.stagesRow}>
          {PIPELINE_STAGES.slice(0, 5).map((stage, index) => {
            const isCompleted = index < currentStageIndex;
            const isCurrent = index === currentStageIndex;
            
            return (
              <div key={stage.id} style={styles.stageIndicator}>
                <div
                  style={{
                    ...styles.stageDot,
                    background: isCompleted ? '#10b981' : isCurrent ? '#3b82f6' : '#e5e7eb',
                    color: isCompleted || isCurrent ? 'white' : '#9ca3af'
                  }}
                >
                  {stage.icon}
                </div>
                <span style={styles.stageLabel}>{stage.label}</span>
              </div>
            );
          })}
        </div>

        <div style={styles.cardMeta}>
          <span>📦 {pipeline.generated_assets?.length || 0} assets</span>
          <span>🎯 {pipeline.brief?.target_platforms?.length || 0} platforms</span>
          <span>📊 {pipeline.progress_percentage || 0}%</span>
        </div>
      </div>
    );
  };

  // Render pipeline details
  const renderPipelineDetails = () => {
    if (!selectedPipeline) return null;

    const pendingAssets = selectedPipeline.generated_assets?.filter(a => a.approved === null) || [];
    const approvedAssets = selectedPipeline.generated_assets?.filter(a => a.approved === true) || [];
    const rejectedAssets = selectedPipeline.generated_assets?.filter(a => a.approved === false) || [];

    return (
      <div style={styles.detailPanel}>
        <div style={styles.detailHeader}>
          <div>
            <h2 style={styles.detailTitle}>{selectedPipeline.brief?.title}</h2>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
              {selectedPipeline.brief?.objective}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            {selectedPipeline.status === 'pending' && (
              <button
                style={{ ...styles.actionButton, ...styles.primaryButton }}
                onClick={() => startPipeline(selectedPipeline.pipeline_id)}
              >
                ▶️ Start Pipeline
              </button>
            )}
            {['pending', 'in_progress', 'paused'].includes(selectedPipeline.status) && (
              <button
                style={{ ...styles.actionButton, ...styles.dangerButton }}
                onClick={() => cancelPipeline(selectedPipeline.pipeline_id)}
              >
                ✖️ Cancel
              </button>
            )}
            <button
              style={{ ...styles.actionButton, ...styles.secondaryButton }}
              onClick={() => setSelectedPipeline(null)}
            >
              Close
            </button>
          </div>
        </div>

        {/* Progress visualization */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Pipeline Progress</h3>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                width: `${selectedPipeline.progress_percentage || 0}%`
              }}
            />
          </div>
          <div style={{ ...styles.stagesRow, marginTop: '16px' }}>
            {PIPELINE_STAGES.map((stage, index) => {
              const currentStageIndex = getStageIndex(selectedPipeline.current_stage);
              const isCompleted = index < currentStageIndex;
              const isCurrent = index === currentStageIndex;
              const stageResult = selectedPipeline.stage_results?.find(r => r.stage === stage.id);
              
              return (
                <div key={stage.id} style={styles.stageIndicator}>
                  <div
                    style={{
                      ...styles.stageDot,
                      background: stageResult?.status === 'failed' ? '#ef4444' :
                        isCompleted ? '#10b981' : isCurrent ? '#3b82f6' : '#e5e7eb',
                      color: isCompleted || isCurrent ? 'white' : '#9ca3af'
                    }}
                  >
                    {stageResult?.status === 'failed' ? '✗' : stage.icon}
                  </div>
                  <span style={styles.stageLabel}>{stage.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Assets section */}
        {selectedPipeline.status === 'awaiting_approval' && pendingAssets.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600' }}>
                Pending Approval ({pendingAssets.length})
              </h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={styles.approveButton}
                  onClick={() => bulkApproveAssets(
                    selectedPipeline.pipeline_id,
                    pendingAssets.map(a => a.asset_id),
                    true
                  )}
                >
                  ✓ Approve All
                </button>
                <button
                  style={styles.rejectButton}
                  onClick={() => bulkApproveAssets(
                    selectedPipeline.pipeline_id,
                    pendingAssets.map(a => a.asset_id),
                    false
                  )}
                >
                  ✗ Reject All
                </button>
              </div>
            </div>
            <div style={styles.assetsGrid}>
              {pendingAssets.map(asset => (
                <div key={asset.asset_id} style={styles.assetCard}>
                  <div style={styles.assetType}>
                    {asset.asset_type} • {asset.platform}
                  </div>
                  <div style={styles.assetContent}>
                    {asset.asset_type === 'copy' && (
                      <>
                        <strong>{asset.content?.headline}</strong>
                        <p style={{ margin: '8px 0', fontSize: '13px', color: '#6b7280' }}>
                          {asset.content?.body?.substring(0, 150)}...
                        </p>
                      </>
                    )}
                    {asset.asset_type === 'image' && (
                      <p style={{ fontSize: '13px', color: '#6b7280' }}>
                        {asset.content?.prompt?.substring(0, 100)}...
                      </p>
                    )}
                    {asset.asset_type === 'video_script' && (
                      <>
                        <strong>{asset.content?.title}</strong>
                        <p style={{ margin: '8px 0', fontSize: '13px', color: '#6b7280' }}>
                          {asset.content?.scenes?.length || 0} scenes
                        </p>
                      </>
                    )}
                  </div>
                  <div style={styles.assetActions}>
                    <button
                      style={styles.approveButton}
                      onClick={() => handleAssetApproval(selectedPipeline.pipeline_id, asset.asset_id, true)}
                    >
                      ✓ Approve
                    </button>
                    <button
                      style={styles.rejectButton}
                      onClick={() => handleAssetApproval(selectedPipeline.pipeline_id, asset.asset_id, false)}
                    >
                      ✗ Reject
                    </button>
                    <button
                      style={{ ...styles.actionButton, ...styles.secondaryButton, padding: '6px 12px', fontSize: '12px' }}
                      onClick={() => {
                        setSelectedAsset(asset);
                        setShowAssetModal(true);
                      }}
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Approved assets */}
        {approvedAssets.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              Approved Assets ({approvedAssets.length})
            </h3>
            <div style={styles.assetsGrid}>
              {approvedAssets.map(asset => (
                <div key={asset.asset_id} style={{ ...styles.assetCard, borderColor: '#10b981' }}>
                  <div style={{ ...styles.assetType, color: '#10b981' }}>
                    ✓ {asset.asset_type} • {asset.platform}
                  </div>
                  <div style={styles.assetContent}>
                    {asset.asset_type === 'copy' && asset.content?.headline}
                    {asset.asset_type === 'image' && 'Image ready'}
                    {asset.asset_type === 'video_script' && asset.content?.title}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Brief details */}
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Brief Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Content Type</div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                {CONTENT_TYPES.find(c => c.value === selectedPipeline.brief?.content_type)?.label}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Platforms</div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                {selectedPipeline.brief?.target_platforms?.join(', ')}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Tone</div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                {selectedPipeline.brief?.tone_of_voice}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Target Audience</div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                {selectedPipeline.brief?.target_audience}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render create modal
  const renderCreateModal = () => {
    if (!showCreateModal) return null;

    return (
      <div style={styles.modal} onClick={() => setShowCreateModal(false)}>
        <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>Create Content Pipeline</h2>
            <button style={styles.closeButton} onClick={() => setShowCreateModal(false)}>×</button>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Campaign Title *</label>
            <input
              style={styles.input}
              value={newPipeline.brief.title}
              onChange={e => setNewPipeline(prev => ({
                ...prev,
                brief: { ...prev.brief, title: e.target.value }
              }))}
              placeholder="e.g., Q1 Product Launch Campaign"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Objective *</label>
            <textarea
              style={styles.textarea}
              value={newPipeline.brief.objective}
              onChange={e => setNewPipeline(prev => ({
                ...prev,
                brief: { ...prev.brief, objective: e.target.value }
              }))}
              placeholder="What do you want to achieve with this content?"
            />
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Content Type *</label>
              <select
                style={styles.select}
                value={newPipeline.brief.content_type}
                onChange={e => setNewPipeline(prev => ({
                  ...prev,
                  brief: { ...prev.brief, content_type: e.target.value }
                }))}
              >
                {CONTENT_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Tone of Voice</label>
              <select
                style={styles.select}
                value={newPipeline.brief.tone_of_voice}
                onChange={e => setNewPipeline(prev => ({
                  ...prev,
                  brief: { ...prev.brief, tone_of_voice: e.target.value }
                }))}
              >
                {TONE_OPTIONS.map(tone => (
                  <option key={tone.value} value={tone.value}>{tone.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Target Platforms *</label>
            <div style={styles.checkboxGroup}>
              {PLATFORMS.map(platform => (
                <label key={platform.value} style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    style={styles.checkbox}
                    checked={newPipeline.brief.target_platforms.includes(platform.value)}
                    onChange={e => {
                      const platforms = e.target.checked
                        ? [...newPipeline.brief.target_platforms, platform.value]
                        : newPipeline.brief.target_platforms.filter(p => p !== platform.value);
                      setNewPipeline(prev => ({
                        ...prev,
                        brief: { ...prev.brief, target_platforms: platforms }
                      }));
                    }}
                  />
                  {platform.icon} {platform.label}
                </label>
              ))}
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Target Audience *</label>
            <input
              style={styles.input}
              value={newPipeline.brief.target_audience}
              onChange={e => setNewPipeline(prev => ({
                ...prev,
                brief: { ...prev.brief, target_audience: e.target.value }
              }))}
              placeholder="e.g., Marketing professionals, 25-45, interested in AI tools"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Key Messages *</label>
            <textarea
              style={styles.textarea}
              value={newPipeline.brief.key_messages.join('\n')}
              onChange={e => setNewPipeline(prev => ({
                ...prev,
                brief: { ...prev.brief, key_messages: e.target.value.split('\n').filter(m => m.trim()) }
              }))}
              placeholder="Enter each key message on a new line"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Call to Action</label>
            <input
              style={styles.input}
              value={newPipeline.brief.call_to_action}
              onChange={e => setNewPipeline(prev => ({
                ...prev,
                brief: { ...prev.brief, call_to_action: e.target.value }
              }))}
              placeholder="e.g., Sign up for free trial"
            />
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Image Style</label>
              <select
                style={styles.select}
                value={newPipeline.config.image_style_preset}
                onChange={e => setNewPipeline(prev => ({
                  ...prev,
                  config: { ...prev.config, image_style_preset: e.target.value }
                }))}
              >
                {STYLE_PRESETS.map(style => (
                  <option key={style.value} value={style.value}>{style.label}</option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Variations per Platform</label>
              <input
                type="number"
                style={styles.input}
                value={newPipeline.config.num_variations}
                onChange={e => setNewPipeline(prev => ({
                  ...prev,
                  config: { ...prev.config, num_variations: parseInt(e.target.value) || 1 }
                }))}
                min="1"
                max="10"
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={newPipeline.config.auto_generate_images}
                  onChange={e => setNewPipeline(prev => ({
                    ...prev,
                    config: { ...prev.config, auto_generate_images: e.target.checked }
                  }))}
                />
                Generate Images
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={newPipeline.config.auto_generate_videos}
                  onChange={e => setNewPipeline(prev => ({
                    ...prev,
                    config: { ...prev.config, auto_generate_videos: e.target.checked }
                  }))}
                />
                Generate Videos
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={newPipeline.config.approval_required}
                  onChange={e => setNewPipeline(prev => ({
                    ...prev,
                    config: { ...prev.config, approval_required: e.target.checked }
                  }))}
                />
                Require Approval
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={newPipeline.auto_start}
                  onChange={e => setNewPipeline(prev => ({
                    ...prev,
                    auto_start: e.target.checked
                  }))}
                />
                Start Immediately
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
            <button
              style={{ ...styles.actionButton, ...styles.secondaryButton }}
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </button>
            <button
              style={{ ...styles.actionButton, ...styles.primaryButton }}
              onClick={createPipeline}
              disabled={!newPipeline.brief.title || !newPipeline.brief.objective || newPipeline.brief.target_platforms.length === 0}
            >
              Create Pipeline
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render asset detail modal
  const renderAssetModal = () => {
    if (!showAssetModal || !selectedAsset) return null;

    return (
      <div style={styles.modal} onClick={() => setShowAssetModal(false)}>
        <div style={{ ...styles.modalContent, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>
              {selectedAsset.asset_type.replace('_', ' ').toUpperCase()} - {selectedAsset.platform}
            </h2>
            <button style={styles.closeButton} onClick={() => setShowAssetModal(false)}>×</button>
          </div>

          {selectedAsset.asset_type === 'copy' && (
            <div>
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>Headline</label>
                <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px', fontSize: '16px', fontWeight: '600' }}>
                  {selectedAsset.content?.headline}
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>Body</label>
                <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>
                  {selectedAsset.content?.body}
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>Call to Action</label>
                <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                  {selectedAsset.content?.cta}
                </div>
              </div>
              {selectedAsset.content?.hashtags?.length > 0 && (
                <div>
                  <label style={styles.label}>Hashtags</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedAsset.content.hashtags.map((tag, i) => (
                      <span key={i} style={{ padding: '4px 8px', background: '#e0e7ff', color: '#4338ca', borderRadius: '4px', fontSize: '12px' }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {selectedAsset.asset_type === 'image' && (
            <div>
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>Prompt</label>
                <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                  {selectedAsset.content?.prompt}
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>Negative Prompt</label>
                <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                  {selectedAsset.content?.negative_prompt}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={styles.label}>Dimensions</label>
                  <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                    {selectedAsset.content?.width} x {selectedAsset.content?.height}
                  </div>
                </div>
                <div>
                  <label style={styles.label}>Style</label>
                  <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                    {selectedAsset.content?.style_preset}
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedAsset.asset_type === 'video_script' && (
            <div>
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>Title</label>
                <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px', fontWeight: '600' }}>
                  {selectedAsset.content?.title}
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>Scenes</label>
                {selectedAsset.content?.scenes?.map((scene, i) => (
                  <div key={i} style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>Scene {scene.scene_number}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>{scene.visual_description}</div>
                    {scene.narration && (
                      <div style={{ fontSize: '13px', fontStyle: 'italic', marginTop: '4px' }}>"{scene.narration}"</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedPipeline && selectedAsset.approved === null && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                style={styles.rejectButton}
                onClick={() => handleAssetApproval(selectedPipeline.pipeline_id, selectedAsset.asset_id, false)}
              >
                ✗ Reject
              </button>
              <button
                style={styles.approveButton}
                onClick={() => handleAssetApproval(selectedPipeline.pipeline_id, selectedAsset.asset_id, true)}
              >
                ✓ Approve
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Content Pipeline</h1>
          <p style={styles.subtitle}>Automate your content generation workflow</p>
        </div>
        <button style={styles.createButton} onClick={() => setShowCreateModal(true)}>
          <span>+</span> Create Pipeline
        </button>
      </div>

      <div style={styles.tabs}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'pipelines' ? styles.activeTab : {})
          }}
          onClick={() => setActiveTab('pipelines')}
        >
          📋 Pipelines
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'analytics' ? styles.activeTab : {})
          }}
          onClick={() => setActiveTab('analytics')}
        >
          📊 Analytics
        </button>
      </div>

      {activeTab === 'analytics' && renderAnalytics()}

      {activeTab === 'pipelines' && (
        <>
          <div style={styles.filterBar}>
            <select
              style={styles.filterSelect}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="awaiting_approval">Awaiting Approval</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {loading ? (
            <div style={styles.emptyState}>
              <div>Loading pipelines...</div>
            </div>
          ) : error ? (
            <div style={{ ...styles.emptyState, color: '#ef4444' }}>
              <div>Error: {error}</div>
            </div>
          ) : pipelines.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📦</div>
              <h3>No pipelines yet</h3>
              <p>Create your first content pipeline to get started</p>
              <button
                style={{ ...styles.createButton, marginTop: '16px' }}
                onClick={() => setShowCreateModal(true)}
              >
                Create Pipeline
              </button>
            </div>
          ) : (
            <div style={styles.grid}>
              {pipelines.map(renderPipelineCard)}
            </div>
          )}
        </>
      )}

      {selectedPipeline && renderPipelineDetails()}
      {renderCreateModal()}
      {renderAssetModal()}
    </div>
  );
};

export default ContentPipeline;
